<template>
  <div>
    <sdPageHeader title="Sandbox Banking">
      <template #subTitle>
        <span class="banking-dashboard__subtitle">Professional sandbox banking workspace for Yapily account connectivity and transaction review.</span>
      </template>
    </sdPageHeader>

    <Main>
      <div class="banking-dashboard">
        <BankConnect
          :loading="state.connecting"
          :disconnecting="state.disconnecting"
          :can-disconnect="Boolean(state.accounts.length)"
          :loading-institutions="state.loadingInstitutions"
          :error="state.connectError"
          :message="state.connectMessage"
          :institutions="state.institutions"
          :selected-institution-id="state.selectedInstitutionId"
          :account-count="state.accounts.length"
          :transaction-count="loadedTransactionCount"
          @update:selectedInstitutionId="state.selectedInstitutionId = $event"
          @connect="connectBank"
          @disconnect="disconnectBank"
        />

        <a-row :gutter="20" class="banking-dashboard__stats">
          <a-col :xs="24" :md="8">
            <div class="banking-stat-card">
              <span class="banking-stat-card__label">Connected accounts</span>
              <strong>{{ state.accounts.length }}</strong>
              <p>Available accounts synced from Yapily consents.</p>
            </div>
          </a-col>
          <a-col :xs="24" :md="8">
            <div class="banking-stat-card">
              <span class="banking-stat-card__label">Active account</span>
              <strong>{{ selectedAccountLabel }}</strong>
              <p>{{ selectedInstitutionLabel }}</p>
            </div>
          </a-col>
          <a-col :xs="24" :md="8">
            <div class="banking-stat-card banking-stat-card--accent">
              <span class="banking-stat-card__label">Transactions loaded</span>
              <strong>{{ loadedTransactionCount }}</strong>
              <p>Stored transactions visible for the current 90 day window.</p>
            </div>
          </a-col>
        </a-row>

        <a-row :gutter="24">
          <a-col :xs="24" :xl="10">
            <BankList
              :accounts="state.accounts"
              :loading="state.loadingAccounts"
              :selected-account-id="state.selectedAccount ? state.selectedAccount.id : null"
              @select="selectAccount"
            />
          </a-col>
          <a-col :xs="24" :xl="14">
            <Transactions
              :account="state.selectedAccount"
              :transactions="state.transactions"
              :loading="state.loadingTransactions"
              :error="state.transactionError"
            />
          </a-col>
        </a-row>
      </div>
    </Main>
  </div>
</template>

<script>
import { computed, defineComponent, onMounted, reactive, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Main } from '../styled';
import BankConnect from '@/components/banking/BankConnect.vue';
import BankList from '@/components/banking/BankList.vue';
import Transactions from '@/components/banking/Transactions.vue';
import YapilyService from '@/services/yapily.service';

export default defineComponent({
  name: 'BankingDashboard',
  components: {
    Main,
    BankConnect,
    BankList,
    Transactions,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const delay = (timeout) => new Promise((resolve) => {
      window.setTimeout(resolve, timeout);
    });
    const state = reactive({
      connecting: false,
      disconnecting: false,
      loadingInstitutions: false,
      loadingAccounts: false,
      loadingTransactions: false,
      connectError: '',
      connectMessage: '',
      transactionError: '',
      institutions: [],
      selectedInstitutionId: '',
      accounts: [],
      transactions: [],
      selectedAccount: null,
    });

    const loadedTransactionCount = computed(() => state.transactions.length);

    const selectedAccountLabel = computed(() => {
      if (!state.selectedAccount) {
        return 'No account selected';
      }

      return state.selectedAccount.account_name || state.selectedAccount.name || 'Unnamed account';
    });

    const selectedInstitutionLabel = computed(() => {
      if (!state.selectedAccount) {
        return 'Choose an account to inspect balances and transactions.';
      }

      return state.selectedAccount.institution_name
        || state.selectedAccount.institution_id
        || 'Institution details unavailable';
    });

    const loadBanks = async () => {
      state.loadingAccounts = true;

      try {
        const response = await YapilyService.getBanks();
        state.accounts = response.data.data || [];

        if (!state.accounts.length) {
          state.selectedAccount = null;
          state.transactions = [];
          return;
        }

        if (!state.selectedAccount && state.accounts.length) {
          await selectAccount(state.accounts[0]);
        }
      } catch (error) {
        state.connectError = error?.response?.data?.message || 'Unable to load connected bank accounts.';
      } finally {
        state.loadingAccounts = false;
      }
    };

    const loadInstitutions = async () => {
      state.loadingInstitutions = true;

      try {
        const response = await YapilyService.getInstitutions('GB');
        state.institutions = response.data.data || [];

        if (!state.selectedInstitutionId && state.institutions.length) {
          state.selectedInstitutionId = state.institutions[0].id;
        }
      } catch (error) {
        state.connectError = error?.response?.data?.message || 'Unable to load available Yapily institutions.';
      } finally {
        state.loadingInstitutions = false;
      }
    };

    const loadTransactions = async (account) => {
      if (!account) {
        state.transactions = [];
        return;
      }

      state.loadingTransactions = true;
      state.transactionError = '';

      try {
        const response = await YapilyService.getTransactions(account.id || account.account_id);
        state.selectedAccount = response.data.account || account;
        state.transactions = response.data.data || [];
      } catch (error) {
        state.transactionError = error?.response?.data?.message || 'Unable to load transactions for the selected account.';
      } finally {
        state.loadingTransactions = false;
      }
    };

    const selectAccount = async (account) => {
      state.selectedAccount = account;
      await loadTransactions(account);
    };

    const resetConnectedData = () => {
      state.accounts = [];
      state.transactions = [];
      state.selectedAccount = null;
      state.transactionError = '';
    };

    const pollConsentUntilComplete = async (consentId, authWindow) => {
      const maxAttempts = 90;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        await delay(2000);

        const response = await YapilyService.getConsentStatus(consentId);
        const payload = response.data || {};

        if (payload.completed && payload.success) {
          if (authWindow && !authWindow.closed) {
            authWindow.close();
          }

          state.connectMessage = `Bank connection complete: ${payload.accounts_synced || 0} account(s) synced.`;
          await loadBanks();
          return;
        }

        if (payload.completed && !payload.success) {
          if (authWindow && !authWindow.closed) {
            authWindow.close();
          }

          throw new Error(payload.message || 'Yapily bank authorisation failed.');
        }

        if (authWindow && authWindow.closed && attempt > 3) {
          throw new Error('The bank authorisation window was closed before the consent completed.');
        }
      }

      if (authWindow && !authWindow.closed) {
        authWindow.close();
      }

      throw new Error('Timed out waiting for Yapily bank authorisation to complete.');
    };

    const connectBank = async () => {
      state.connecting = true;
      state.connectError = '';
      state.connectMessage = '';

      try {
        const response = await YapilyService.connectBank({
          country_code: 'GB',
          institution_id: state.selectedInstitutionId || undefined,
        });
        const redirectUrl = response.data.redirect_url;
        const consentId = response.data.consent_id;

        if (!redirectUrl) {
          throw new Error(response.data.message || 'Yapily did not return a redirect URL.');
        }

        if (!consentId) {
          throw new Error('Yapily did not return a consent ID for polling.');
        }

        const authWindow = window.open(
          redirectUrl,
          'yapily-bank-connect',
          'popup,width=520,height=760,resizable=yes,scrollbars=yes',
        );

        if (!authWindow) {
          throw new Error('Unable to open the bank authorisation window. Allow pop-ups and try again.');
        }

        state.connectMessage = 'Complete the bank authorisation in the popup window. We will finish syncing automatically.';
        await pollConsentUntilComplete(consentId, authWindow);
      } catch (error) {
        state.connectError = error?.response?.data?.message || error.message || 'Unable to create a Yapily connect session.';
      } finally {
        state.connecting = false;
      }
    };

    const disconnectBank = async () => {
      if (!state.accounts.length) {
        return;
      }

      const confirmed = window.confirm('Disconnect Yapily and clear the stored Yapily accounts, transactions, and consent tokens for this user?');

      if (!confirmed) {
        return;
      }

      state.disconnecting = true;
      state.connectError = '';
      state.connectMessage = '';

      try {
        const response = await YapilyService.disconnectBank();
        const warnings = response?.data?.warnings || [];

        resetConnectedData();
        await loadBanks();
        state.connectMessage = warnings.length
          ? `${response.data.message} ${warnings.join(' ')}`
          : response.data.message;
      } catch (error) {
        state.connectError = error?.response?.data?.message || 'Unable to disconnect Yapily accounts.';
      } finally {
        state.disconnecting = false;
      }
    };

    const hydrateFromCallback = async () => {
      const { status, message, accounts } = route.query;

      if (status === 'success') {
        state.connectError = '';
        state.connectMessage = `Bank connection complete${accounts ? `: ${accounts} account(s) synced.` : '.'}`;
        await loadBanks();
      }

      if (status === 'error') {
        state.connectMessage = '';
        state.connectError = message || 'The Yapily callback returned an error.';
      }

      if (route.name === 'banking-callback') {
        router.replace({ name: 'banking' });
      }
    };

    onMounted(async () => {
      await loadInstitutions();
      await loadBanks();
      await hydrateFromCallback();
    });

    watch(
      () => route.fullPath,
      async () => {
        await hydrateFromCallback();
      },
    );

    return {
      state,
      connectBank,
      disconnectBank,
      loadedTransactionCount,
      selectedAccountLabel,
      selectedInstitutionLabel,
      selectAccount,
    };
  },
});
</script>

<style scoped>
.banking-dashboard {
  display: grid;
  gap: 24px;
  position: relative;
}

.banking-dashboard::before {
  content: '';
  position: absolute;
  inset: 18px 0 auto 0;
  height: 240px;
  border-radius: 28px;
  background:
    radial-gradient(circle at top left, rgba(15, 118, 110, 0.14), transparent 38%),
    radial-gradient(circle at top right, rgba(37, 99, 235, 0.12), transparent 42%),
    linear-gradient(180deg, #f8fbff 0%, rgba(248, 251, 255, 0) 100%);
  pointer-events: none;
  z-index: 0;
}

.banking-dashboard > * {
  position: relative;
  z-index: 1;
}

.banking-dashboard__subtitle {
  color: #526072;
}

.banking-dashboard__stats {
  margin-top: -4px;
}

.banking-stat-card {
  min-height: 148px;
  padding: 22px 24px;
  border: 1px solid #d9e4ef;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.06);
  backdrop-filter: blur(10px);
}

.banking-stat-card--accent {
  border-color: rgba(15, 118, 110, 0.22);
  background: linear-gradient(180deg, rgba(240, 253, 250, 0.96) 0%, rgba(255, 255, 255, 0.96) 100%);
}

.banking-stat-card__label {
  display: inline-block;
  margin-bottom: 14px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #64748b;
}

.banking-stat-card strong {
  display: block;
  margin-bottom: 10px;
  font-size: 28px;
  line-height: 1.15;
  color: #0f172a;
}

.banking-stat-card p {
  margin: 0;
  color: #5f6f82;
  line-height: 1.55;
}

@media only screen and (max-width: 767px) {
  .banking-stat-card {
    min-height: auto;
  }
}
</style>